// react imports
import { useState } from "react";

// common imports
import FlexColumn from "common/components/styled-components/FlexColumn";
import Tooltip from "common/components/Tooltip";
import TreeIcon from "common/svg/TreeIcon";
import CarIcon from "common/svg/CarIcon";

// styled-components imports
import Container from "./styled-components/Container";
import Description from "./styled-components/Description";
import DescriptionContent from "./styled-components/DescriptionContent";
import EquivalenceDetails from "./styled-components/EquivalenceDetails";
import Equivalences from "./styled-components/Equivalences";
import IconGrid from "./styled-components/IconGrid";
import PopUp from "./styled-components/PopUp";
import PopUpContainer from "./styled-components/PopUpContainer";
import Title from "./styled-components/Title";
import PopUpContent from "./styled-components/PopUpContent";

interface FootprintEquivalencesProps {
  treeEquivalence: number;
  carEquivalence: number;
  userFootprint: number;
}

function FootprintEquivalences({
  treeEquivalence,
  carEquivalence,
  userFootprint,
}: FootprintEquivalencesProps) {
  const [isTreePopUpOpen, setIsTreePopUpOpen] = useState(false);
  const [isCarPopUpOpen, setIsCarPopUpOpen] = useState(false);

  const treeIcons: React.ReactNode[] = [];
  const carIcons: React.ReactNode[] = [];

  for (let i = 0; i < 5 * 3; i++) {
    treeIcons.push(
      <TreeIcon
        key={`tree-icon__${i}`}
        color={
          i < Math.round(userFootprint / (450 / 1000)) ? "#a6be0a" : "#cfd3af"
        }
      />
    );
  }

  for (let i = 0; i < 4 * 3; i++) {
    carIcons.push(
      <CarIcon
        key={`car-icon__${i}`}
        color={
          i < Math.round(userFootprint / (560 / 1000)) ? "#1a5690" : "#8eadbc"
        }
      />
    );
  }

  function handleTreeTooltip() {
    setIsCarPopUpOpen(false);
    setIsTreePopUpOpen(!isTreePopUpOpen);
  }

  function handleCarTooltip() {
    setIsTreePopUpOpen(false);
    setIsCarPopUpOpen(!isCarPopUpOpen);
  }

  return (
    <Container>
      <Title>
        <span>Tu huella en el planeta</span>
      </Title>
      <Equivalences>
        <FlexColumn>
          <PopUpContainer>
            {isTreePopUpOpen && (
              <PopUp>
                <PopUpContent>
                  Para este cálculo se ha utilizado el "Pinus Pinea" o pino
                  piñonero, el cual absorbe 60 kg de dióxido de carbono al año.
                  <br />
                  Fuente:{" "}
                  <a
                    href="https://www.miteco.gob.es/es/cambio-climatico/temas/mitigacion-politicas-y-medidas/guiapa_tcm30-479094.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Absoción de Dióxico de Carbono, Mayo 2019
                  </a>
                </PopUpContent>
              </PopUp>
            )}
          </PopUpContainer>
          <EquivalenceDetails>
            <IconGrid columns={5}>{treeIcons}</IconGrid>
            <Description>
              <DescriptionContent>
                Tus toneladas de CO2 equivalen a {treeEquivalence} árboles
              </DescriptionContent>
              <Tooltip color="#232323" onClick={handleTreeTooltip} />
            </Description>
          </EquivalenceDetails>
        </FlexColumn>
        <FlexColumn>
          <PopUpContainer>
            {isCarPopUpOpen && (
              <PopUp>
                <PopUpContent>
                  Calculado en base a un viaje en coche de 100km equivalente a
                  0,19 kg de CO<sub>2</sub>.
                  <br />
                  Referencia:{" "}
                  <a
                    href="https://www.miteco.gob.es/es/cambio-climatico/temas/mitigacion-politicas-y-medidas/factoresemision_tcm30-479095.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Factores de Emisión, Abril 2021
                  </a>
                </PopUpContent>
              </PopUp>
            )}
          </PopUpContainer>
          <EquivalenceDetails>
            <IconGrid columns={4}>{carIcons}</IconGrid>
            <Description>
              <DescriptionContent>
                Tu huella de carbono equivale a la contaminación diaria de{" "}
                {carEquivalence} coches
              </DescriptionContent>
              <Tooltip color="#232323" onClick={handleCarTooltip} />
            </Description>
          </EquivalenceDetails>
        </FlexColumn>
      </Equivalences>
    </Container>
  );
}

export default FootprintEquivalences;
